import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useIsPreActiveCustomer } from '../../../../../../../hooks/useIsPreActiveCustomer';

/**
 * The single source of truth for which year the Accounting hub is showing.
 *
 * Both `AccountingHome` and `AccountingYearPicker` need it, and each used to
 * parse `?year=` for itself — so the cards and the Select could disagree.
 *
 * Two rules live here:
 *
 * 1. `?year=abc` parses to `NaN`. That used to reach the server, which rejected
 *    it; it now reaches the demo generators, where it turns every total into
 *    `NaN` and renders "$NaN" on every card — `formatCurrency` has no guard,
 *    because `NaN < 0` is false. Anything non-finite falls back to the default,
 *    and anything out of range is clamped to a year the picker actually offers.
 *
 * 2. Pre-active customers default to the last *completed* year. The demo
 *    generators emit full-year figures, so defaulting to the current year books
 *    a whole year of revenue and 715 transactions into an incomplete one —
 *    starkest in January, and it's the first screen they see. This also lines
 *    the default up with the newest demo report pack.
 */
export function useAccountingYear(earliestYear: number): number {
  const [params] = useSearchParams();
  const isPreActive = useIsPreActiveCustomer();
  const currentYear = dayjs().year();

  const clamp = (year: number) =>
    Math.min(currentYear, Math.max(earliestYear, year));

  const raw = params.get('year');
  const parsed = raw ? parseInt(raw, 10) : NaN;

  return Number.isFinite(parsed)
    ? clamp(parsed)
    : clamp(isPreActive ? currentYear - 1 : currentYear);
}
